import { useMemo } from 'react'
import type { AgentOutputProps } from '../types'

/**
 * ActionItemsRenderer — used by meeting-action-extractor.
 *
 * Shows a table of { title, assignee, due, priority } rows with priority
 * badges (p0 red → p4 grey). Falls back to pretty-printed JSON if the
 * result doesn't conform.
 */

type Priority = 'p0' | 'p1' | 'p2' | 'p3' | 'p4'

interface ActionItem {
  title: string
  assignee?: string | null
  due?: string | null
  priority?: Priority | string | null
  notes?: string
}

const PRIORITY_META: Record<string, { color: string; bg: string; label: string }> = {
  p0: { color: '#EF4444', bg: '#EF444420', label: 'P0' },
  p1: { color: '#F59E0B', bg: '#F59E0B20', label: 'P1' },
  p2: { color: '#3B82F6', bg: '#3B82F620', label: 'P2' },
  p3: { color: '#8B5CF6', bg: '#8B5CF620', label: 'P3' },
  p4: { color: '#6B7280', bg: '#6B728020', label: 'P4' },
}

function extractItems(result: any): ActionItem[] | null {
  const r = result || {}
  const ad = r.agent_data || {}
  for (const src of [r, ad]) {
    if (Array.isArray(src.action_items)) return src.action_items
    if (Array.isArray(src.actions)) return src.actions
  }
  // Try parsing output text as JSON
  if (typeof r.output === 'string') {
    try {
      const parsed = JSON.parse(r.output)
      if (Array.isArray(parsed?.action_items)) return parsed.action_items
    } catch {}
  }
  return null
}

export default function ActionItemsRenderer(props: AgentOutputProps) {
  const items = useMemo(() => extractItems(props.result), [props.result])

  if (!items || items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No action items found in the agent result.
      </div>
    )
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)_minmax(0,1fr)_80px] gap-0 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold px-4 py-2 bg-muted/40 border-b border-border">
        <div>Action</div>
        <div>Assignee</div>
        <div>Due</div>
        <div>Priority</div>
      </div>
      {items.map((item, i) => {
        const pri = (item.priority || 'p2').toString().toLowerCase()
        const meta = PRIORITY_META[pri] ?? PRIORITY_META.p2
        return (
          <div
            key={i}
            className="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)_minmax(0,1fr)_80px] gap-0 px-4 py-3 border-b border-border/60 last:border-0 text-sm"
          >
            <div className="min-w-0">
              <div className="font-medium text-foreground truncate">{item.title}</div>
              {item.notes && (
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{item.notes}</div>
              )}
            </div>
            <div className="text-xs text-foreground self-center truncate">
              {item.assignee || <span className="text-muted-foreground italic">unassigned</span>}
            </div>
            <div className="text-xs self-center">
              {item.due ? (
                <span className="font-mono text-foreground">{item.due}</span>
              ) : (
                <span className="text-muted-foreground italic">—</span>
              )}
            </div>
            <div className="self-center">
              <span
                className="inline-block px-2 py-0.5 rounded-full text-xs font-bold"
                style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.color}40` }}
              >
                {meta.label}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
